import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CText } from '../../components/common';
import { PortfolioHealthCheckResult } from '../../api/portfolio';
import {
  FiUploadCloud,
  FiAlertTriangle,
  FiRefreshCw,
  FiChevronDown,
  FiChevronRight,
  FiUsers,
  FiShield,
  FiPieChart,
  FiTrendingUp,
  FiTarget,
} from 'react-icons/fi';

interface Props {
  result: PortfolioHealthCheckResult | null;
  loading: boolean;
  error: string | null;
  onRunAnalysis: () => void;
  hasHoldings: boolean;
  colors: any;
}

const PortfolioHealthCheck: React.FC<Props> = ({ result, loading, error, onRunAnalysis, hasHoldings, colors }) => {
  const navigate = useNavigate();
  const [expandedFlags, setExpandedFlags] = useState<Set<number>>(new Set());

  const toggleFlag = (index: number) => {
    setExpandedFlags((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  // Color for health score
  const getScoreColor = (score: number) => {
    if (score >= 75) return '#22c55e';
    if (score >= 50) return '#f59e0b';
    return '#ef4444';
  };

  const getSeverityColor = (severity: string) => {
    if (severity === 'high') return '#ef4444';
    if (severity === 'medium') return '#f59e0b';
    return '#22c55e';
  };

  // Empty state — no holdings
  if (!hasHoldings) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
          style={{ backgroundColor: colors.primary + '15' }}
        >
          {React.createElement(FiUploadCloud as any, { size: 28, color: colors.primary })}
        </div>
        <CText type="B18" color={colors.textColor} style={{ marginBottom: 6, textAlign: 'center' }}>
          Upload your portfolio first
        </CText>
        <CText type="r14" color={colors.grayScale500} style={{ textAlign: 'center', maxWidth: 340 }}>
          Import your broker holdings to run an AI-powered health check on your portfolio.
        </CText>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="space-y-4">
        {/* Skeleton health score */}
        <div className="rounded-2xl p-6 flex items-center gap-6 animate-pulse" style={{ backgroundColor: colors.inputBackground }}>
          <div className="w-24 h-24 rounded-full" style={{ backgroundColor: colors.grayScale500 + '20' }} />
          <div className="flex-1 space-y-3">
            <div className="h-5 rounded-full w-3/4" style={{ backgroundColor: colors.grayScale500 + '20' }} />
            <div className="h-4 rounded-full w-1/2" style={{ backgroundColor: colors.grayScale500 + '15' }} />
          </div>
        </div>
        {/* Skeleton cards */}
        <div className="grid grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-xl p-4 animate-pulse" style={{ backgroundColor: colors.inputBackground }}>
              <div className="h-3 rounded w-1/2 mb-2" style={{ backgroundColor: colors.grayScale500 + '20' }} />
              <div className="h-6 rounded w-2/3" style={{ backgroundColor: colors.grayScale500 + '15' }} />
            </div>
          ))}
        </div>
        <div className="text-center mt-4">
          <CText type="r14" color={colors.grayScale500}>Analyzing your portfolio... This may take a minute.</CText>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
          style={{ backgroundColor: '#f59e0b20' }}
        >
          {React.createElement(FiAlertTriangle as any, { size: 28, color: '#f59e0b' })}
        </div>
        <CText type="B16" color={colors.textColor} style={{ marginBottom: 6, textAlign: 'center' }}>
          Analysis failed
        </CText>
        <CText type="r14" color={colors.grayScale500} style={{ marginBottom: 16, textAlign: 'center', maxWidth: 340 }}>
          {error}
        </CText>
        <button
          onClick={onRunAnalysis}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium"
          style={{ backgroundColor: colors.primary + '15', color: colors.primary }}
        >
          {React.createElement(FiRefreshCw as any, { size: 14 })}
          Retry Analysis
        </button>
      </div>
    );
  }

  // No result yet — prompt to run
  if (!result) {
    const previewCards = [
      { icon: FiShield, title: 'Health Score', value: '0-100', desc: 'Overall portfolio rating' },
      { icon: FiPieChart, title: 'Sector Split', value: 'Visual chart', desc: 'Where your money is concentrated' },
      { icon: FiAlertTriangle, title: 'Risk Flags', value: 'Auto-detected', desc: 'Concentration & quality warnings' },
      { icon: FiTarget, title: 'Action Items', value: 'Personalized', desc: 'Steps to improve your portfolio' },
    ];

    return (
      <div className="space-y-6">
        {/* CTA section */}
        <div className="flex flex-col items-center text-center py-8">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
            style={{ backgroundColor: colors.primary + '15' }}
          >
            {React.createElement(FiShield as any, { size: 28, color: colors.primary })}
          </div>
          <CText type="B18" color={colors.textColor} style={{ marginBottom: 6 }}>
            Get your portfolio health score
          </CText>
          <CText type="r14" color={colors.grayScale500} style={{ marginBottom: 20, maxWidth: 380 }}>
            Our AI analyzes every stock against real financial data — ROE, P/E, sector allocation, and more.
          </CText>
          <button
            onClick={onRunAnalysis}
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-base transition-all duration-200 hover:-translate-y-0.5"
            style={{
              background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
              color: '#fff',
              boxShadow: colors.shadowMedium,
            }}
          >
            {React.createElement(FiShield as any, { size: 18 })}
            Analyze My Portfolio
          </button>
        </div>

        {/* Preview cards */}
        <div className="grid grid-cols-2 gap-3">
          {previewCards.map((card) => (
            <div
              key={card.title}
              className="rounded-xl p-4 border"
              style={{ backgroundColor: colors.inputBackground, borderColor: colors.borderColor }}
            >
              <div className="flex items-center gap-2 mb-2">
                {React.createElement(card.icon as any, { size: 14, color: colors.primary })}
                <CText type="r12" color={colors.grayScale500}>{card.title}</CText>
              </div>
              <CText type="B16" color={colors.textColor} style={{ marginBottom: 2 }}>{card.value}</CText>
              <CText type="r12" color={colors.grayScale400}>{card.desc}</CText>
            </div>
          ))}
        </div>

        {/* Reassurance dots */}
        <div className="flex items-center justify-center gap-6">
          <CText type="r12" color={colors.grayScale400}>Takes ~30 seconds</CText>
          <span style={{ color: colors.grayScale400 }}>·</span>
          <CText type="r12" color={colors.grayScale400}>Results cached for 1 hour</CText>
        </div>
      </div>
    );
  }

  // --- Main result display ---
  const scoreColor = getScoreColor(result.health_score);

  return (
    <div className="space-y-4">
      {/* A. Health Score Banner */}
      <div
        className="rounded-2xl p-5 md:p-6 flex items-center gap-5"
        style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}
      >
        {/* Score circle */}
        <div className="relative flex-shrink-0">
          <svg width="96" height="96" viewBox="0 0 96 96">
            <circle cx="48" cy="48" r="42" fill="none" stroke={colors.borderColor} strokeWidth="8" />
            <circle
              cx="48" cy="48" r="42"
              fill="none"
              stroke={scoreColor}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={`${(result.health_score / 100) * 264} 264`}
              transform="rotate(-90 48 48)"
              style={{ transition: 'stroke-dasharray 1s ease-out' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl font-bold" style={{ color: scoreColor }}>
              {result.health_score}
            </span>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <CText type="B18" color={colors.textColor} style={{ marginBottom: 4 }}>
            Portfolio Health Score
          </CText>
          <CText type="r14" color={colors.grayScale500} style={{ lineHeight: 1.5 }}>
            {result.summary}
          </CText>
          <div className="flex items-center gap-3 mt-2">
            <CText type="r12" color={colors.grayScale400}>
              {result.stocks_analyzed} stocks analyzed
            </CText>
            {result.stocks_not_found.length > 0 && (
              <CText type="r12" color="#f59e0b">
                {result.stocks_not_found.length} not found
              </CText>
            )}
          </div>
        </div>
      </div>

      {/* B. Sector Breakdown */}
      <div
        className="rounded-2xl p-5 border"
        style={{ backgroundColor: colors.inputBackground, borderColor: colors.borderColor }}
      >
        <div className="flex items-center gap-2 mb-4">
          {React.createElement(FiPieChart as any, { size: 16, color: colors.primary })}
          <CText type="M14" color={colors.textColor} style={{ fontWeight: 600 }}>Sector Breakdown</CText>
        </div>

        {/* Stacked bar */}
        <div className="flex rounded-full overflow-hidden h-4 mb-4" style={{ backgroundColor: colors.borderColor }}>
          {result.sector_breakdown.map((s, i) => {
            const barColors = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6'];
            return (
              <div
                key={s.sector}
                style={{
                  width: `${s.percentage}%`,
                  backgroundColor: barColors[i % barColors.length],
                  minWidth: s.percentage > 2 ? undefined : '4px',
                }}
                title={`${s.sector}: ${s.percentage}%`}
              />
            );
          })}
        </div>

        {/* Legend */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {result.sector_breakdown.map((s, i) => {
            const barColors = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6'];
            return (
              <div key={s.sector} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: barColors[i % barColors.length] }}
                />
                <CText type="r12" color={colors.textColor}>
                  {s.sector} ({s.percentage}%)
                </CText>
              </div>
            );
          })}
        </div>
      </div>

      {/* C. Risk Summary Cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl p-4 border" style={{ backgroundColor: colors.inputBackground, borderColor: colors.borderColor }}>
          <div className="flex items-center gap-1.5 mb-2">
            {React.createElement(FiPieChart as any, { size: 14, color: colors.grayScale500 })}
            <CText type="r12" color={colors.grayScale500}>Sector Concentration</CText>
          </div>
          <CText type="B18" color={
            result.sector_breakdown[0]?.percentage > 35 ? '#ef4444' :
            result.sector_breakdown[0]?.percentage > 25 ? '#f59e0b' : colors.textColor
          }>
            {result.sector_breakdown[0]?.percentage ?? 0}%
          </CText>
          <CText type="r10" color={colors.grayScale400}>
            {result.sector_breakdown[0]?.sector ?? 'N/A'} (top sector)
          </CText>
        </div>

        <div className="rounded-xl p-4 border" style={{ backgroundColor: colors.inputBackground, borderColor: colors.borderColor }}>
          <div className="flex items-center gap-1.5 mb-2">
            {React.createElement(FiTarget as any, { size: 14, color: colors.grayScale500 })}
            <CText type="r12" color={colors.grayScale500}>Top 3 Concentration</CText>
          </div>
          <CText type="B18" color={
            result.top_holdings_concentration > 60 ? '#ef4444' :
            result.top_holdings_concentration > 50 ? '#f59e0b' : colors.textColor
          }>
            {result.top_holdings_concentration}%
          </CText>
          <CText type="r10" color={colors.grayScale400}>of total portfolio value</CText>
        </div>

        <div className="rounded-xl p-4 border" style={{ backgroundColor: colors.inputBackground, borderColor: colors.borderColor }}>
          <div className="flex items-center gap-1.5 mb-2">
            {React.createElement(FiTrendingUp as any, { size: 14, color: colors.grayScale500 })}
            <CText type="r12" color={colors.grayScale500}>Avg ROE</CText>
          </div>
          <CText type="B18" color={
            result.avg_roe < 8 ? '#ef4444' :
            result.avg_roe < 15 ? '#f59e0b' : '#22c55e'
          }>
            {result.avg_roe}%
          </CText>
          <CText type="r10" color={colors.grayScale400}>weighted by value</CText>
        </div>

        <div className="rounded-xl p-4 border" style={{ backgroundColor: colors.inputBackground, borderColor: colors.borderColor }}>
          <div className="flex items-center gap-1.5 mb-2">
            {React.createElement(FiTarget as any, { size: 14, color: colors.grayScale500 })}
            <CText type="r12" color={colors.grayScale500}>Avg P/E</CText>
          </div>
          <CText type="B18" color={
            result.avg_pe > 30 ? '#f59e0b' : colors.textColor
          }>
            {result.avg_pe}x
          </CText>
          <CText type="r10" color={colors.grayScale400}>weighted by value</CText>
        </div>
      </div>

      {/* D. Risk Flags */}
      {result.risk_flags.length > 0 && (
        <div
          className="rounded-2xl p-5 border"
          style={{ backgroundColor: colors.inputBackground, borderColor: colors.borderColor }}
        >
          <div className="flex items-center gap-2 mb-4">
            {React.createElement(FiAlertTriangle as any, { size: 16, color: '#f59e0b' })}
            <CText type="M14" color={colors.textColor} style={{ fontWeight: 600 }}>
              Risk Flags ({result.risk_flags.length})
            </CText>
          </div>
          <div className="space-y-2">
            {result.risk_flags
              .sort((a, b) => {
                const order = { high: 0, medium: 1, low: 2 };
                return (order[a.severity] ?? 2) - (order[b.severity] ?? 2);
              })
              .map((flag, i) => {
                const isExpanded = expandedFlags.has(i);
                const sevColor = getSeverityColor(flag.severity);
                return (
                  <div key={i}>
                    <button
                      onClick={() => toggleFlag(i)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors"
                      style={{ backgroundColor: sevColor + '08' }}
                    >
                      <div
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: sevColor }}
                      />
                      <div className="flex-1 min-w-0">
                        <CText type="M12" color={colors.textColor} style={{ fontWeight: 600 }}>
                          {flag.title}
                        </CText>
                      </div>
                      <span
                        className="px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0"
                        style={{ backgroundColor: sevColor + '20', color: sevColor }}
                      >
                        {flag.severity}
                      </span>
                      {React.createElement(
                        isExpanded ? FiChevronDown : FiChevronRight as any,
                        { size: 14, color: colors.grayScale500 }
                      )}
                    </button>
                    {isExpanded && (
                      <div className="px-8 pb-3">
                        <CText type="r12" color={colors.grayScale500} style={{ marginBottom: 4, lineHeight: 1.5 }}>
                          {flag.description}
                        </CText>
                        {flag.affected_stocks.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {flag.affected_stocks.map((s) => (
                              <span
                                key={s}
                                className="px-2 py-0.5 rounded-full text-xs"
                                style={{ backgroundColor: colors.primary + '10', color: colors.primary }}
                              >
                                {s}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* E. AI Recommendations */}
      {result.recommendations.length > 0 && (
        <div
          className="rounded-2xl p-5 border"
          style={{ backgroundColor: colors.inputBackground, borderColor: colors.borderColor }}
        >
          <CText type="M14" color={colors.textColor} style={{ fontWeight: 600, marginBottom: 12 }}>
            Recommendations
          </CText>
          <div className="space-y-2.5">
            {result.recommendations.map((rec, i) => (
              <div key={i} className="flex gap-3">
                <span
                  className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold"
                  style={{ backgroundColor: colors.primary + '15', color: colors.primary }}
                >
                  {i + 1}
                </span>
                <CText type="r14" color={colors.textColor} style={{ lineHeight: 1.5 }}>
                  {rec}
                </CText>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* F. Expert CTA */}
      <div
        className="rounded-2xl p-5 text-center"
        style={{
          background: `linear-gradient(135deg, ${colors.gradient1} 0%, ${colors.gradient2} 100%)`,
        }}
      >
        <CText type="B16" color={colors.white} style={{ marginBottom: 4 }}>
          Get personalized rebalancing advice
        </CText>
        <CText type="r12" color="rgba(255,255,255,0.7)" style={{ marginBottom: 12 }}>
          Talk to a SEBI-registered expert about optimizing your portfolio
        </CText>
        <button
          onClick={() => navigate('/experts')}
          className="px-5 py-2.5 rounded-xl font-medium text-sm transition-all hover:-translate-y-0.5"
          style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: colors.white, backdropFilter: 'blur(8px)' }}
        >
          {React.createElement(FiUsers as any, { size: 14, style: { display: 'inline', marginRight: 6, verticalAlign: 'middle' } })}
          Find an Expert
        </button>
      </div>

      {/* Re-run button */}
      <div className="text-center">
        <button
          onClick={onRunAnalysis}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm"
          style={{ color: colors.grayScale500 }}
        >
          {React.createElement(FiRefreshCw as any, { size: 14 })}
          Re-run analysis
        </button>
      </div>
    </div>
  );
};

export default PortfolioHealthCheck;
